/**
 * Bill Validation — client report context / send API client.
 *
 * Talks to `/api/bill-validation/report`. The report document is rendered by
 * `BillReportScreen.tsx` in the browser and captured there — to PDF, or to a
 * standalone HTML copy when Super Admin has chosen that — then uploaded with the
 * send.
 */

import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import { sysConfigApiService } from '../../scopingAi/services/sysConfigApiService';
import {
  REPORT_ATTACHMENT_CONFIG,
  reportAttachmentFormat,
  type ReportAttachmentFormat,
} from '../lib/reportAttachment';
import type { ReportBatchItemView, ReportBatchSummary } from '../lib/reportBatchTypes';

/** Vercel rejects a request body over 4.5 MB; kept clear of it for multipart overhead. */
const REQUEST_BODY_BUDGET = 4_200_000;

const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001') + '/api/bill-validation/report';

export type BvReportRecipientSuggestion = {
  email: string;
  name: string;
  reason: string;
};

/** A covering-email body, `bill_validation` category of `email_templates`. */
export type BvEmailTemplate = {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  is_default: boolean;
};

export type BvReportContext = {
  /**
   * The bill's figures, ready for `{token}` fill. The dialog runs the chosen
   * email template's subject and body through these, so the covering message
   * quotes the same numbers as the report attached to it.
   */
  tokens: Record<string, string>;
  suggestedRecipients: BvReportRecipientSuggestion[];
  meta: {
    billId: string;
    siteId: string;
    clientId: string | null;
    clientName: string;
    nmi: string;
    periodFrom: string;
    periodTo: string;
    warnings: string[];
    matchWarning: string | null;
    isOvercharge: boolean;
  };
};

export type BvBillsMailbox = {
  id: string;
  email: string;
  is_active: boolean;
  created_at: string;
};

export type BvReportSend = {
  id: string;
  recipient_emails: string[];
  cc_emails: string[];
  subject: string | null;
  status: 'queued' | 'sent' | 'failed';
  error: string | null;
  sent_at: string | null;
  created_at: string;
  pdf_storage_path: string | null;
  gmail_message_id: string | null;
  account?: { email?: string } | null;
  template?: { name?: string } | null;
};

export type BvReportSendResult = {
  sendId: string;
  gmailMessageId: string | null;
  pdfStoragePath: string;
  sentFrom: string;
  recipients: string[];
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'x-api-key': 'scopingai',
    ...(session?.user?.id ? { 'x-user-id': session.user.id } : {}),
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

/**
 * A request can die upstream of our Express handler (gateway timeout, proxy
 * error page), which comes back as HTML rather than JSON. Parsing as text first
 * keeps a raw `JSON.parse` message off the screen.
 */
async function request<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
    const text = await res.text();
    let json: { success?: boolean; data?: unknown; error?: string };
    try {
      json = JSON.parse(text);
    } catch {
      return { success: false, error: `Request failed (${res.status}). The server did not return a valid response.` };
    }
    if (!res.ok || !json.success) return { success: false, error: json.error ?? `Request failed (${res.status})` };
    return { success: true, data: json.data as T };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
}

/** As `request`, but the caller supplies the headers (used for multipart). */
async function requestRaw<T>(path: string, init: RequestInit): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(`${API_BASE}${path}`, init);
    const text = await res.text();
    let json: { success?: boolean; data?: unknown; error?: string; code?: unknown };
    try {
      json = JSON.parse(text);
    } catch {
      return { success: false, error: `Request failed (${res.status}). The server did not return a valid response.` };
    }
    if (!res.ok || !json.success) {
      return {
        success: false,
        error: json.error ?? `Request failed (${res.status})`,
        ...(typeof json.code === 'string' ? { code: json.code } : {}),
      };
    }
    return { success: true, data: json.data as T };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
}

export const bvReportService = {
  context: (billId: string): Promise<ApiResponse<BvReportContext>> =>
    request<BvReportContext>('/context', {
      method: 'POST',
      body: JSON.stringify({ billId }),
    }),

  listMailboxes: (): Promise<ApiResponse<BvBillsMailbox[]>> =>
    request<BvBillsMailbox[]>('/accounts'),

  listEmailTemplates: (): Promise<ApiResponse<BvEmailTemplate[]>> =>
    request<BvEmailTemplate[]>('/email-templates'),

  createEmailTemplate: (input: {
    name: string; subject: string; body_html: string; is_default?: boolean;
  }): Promise<ApiResponse<BvEmailTemplate>> =>
    request<BvEmailTemplate>('/email-templates', { method: 'POST', body: JSON.stringify(input) }),

  updateEmailTemplate: (id: string, input: {
    name?: string; subject?: string; body_html?: string; is_default?: boolean;
  }): Promise<ApiResponse<BvEmailTemplate>> =>
    request<BvEmailTemplate>(`/email-templates/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  deleteEmailTemplate: (id: string): Promise<ApiResponse<null>> =>
    request<null>(`/email-templates/${id}`, { method: 'DELETE' }),

  /**
   * `reportPdf` is the report captured from the screen. It is uploaded rather
   * than re-rendered on the server, so what the client receives is byte-for-byte
   * what the adviser was looking at when they pressed send. `report` is the PDF
   * or the HTML copy, whichever `attachmentFormat()` returned — never both.
   */
  send: async (payload: {
    billId: string;
    accountId?: string | null;
    recipients: string[];
    cc?: string[];
    subject: string;
    bodyHtml: string;
    attachInvoice?: boolean;
    report: { format: ReportAttachmentFormat; file: Blob };
  }): Promise<ApiResponse<BvReportSendResult>> => {
    const form = new FormData();
    form.append('billId', payload.billId);
    if (payload.accountId) form.append('accountId', payload.accountId);
    form.append('recipients', payload.recipients.join(','));
    form.append('cc', (payload.cc ?? []).join(','));
    form.append('subject', payload.subject);
    form.append('bodyHtml', payload.bodyHtml);
    form.append('attachInvoice', String(payload.attachInvoice ?? false));
    // Vercel answers 413 to anything over its limit before the route runs,
    // which reaches the user as "the server did not return a valid response".
    if (payload.report.file.size > REQUEST_BODY_BUDGET) {
      return {
        success: false,
        error: `The report ${payload.report.format.toUpperCase()} is ${(payload.report.file.size / 1e6).toFixed(1)} MB — too large to upload (limit about 4 MB)`,
      };
    }
    if (payload.report.format === 'html') {
      form.append('reportHtml', payload.report.file, 'bill-validation-report.html');
    } else {
      form.append('report', payload.report.file, 'bill-validation-report.pdf');
    }

    // Content-Type is omitted on purpose — the browser has to set it itself so
    // the multipart boundary is included.
    const { 'Content-Type': _drop, ...headers } = await authHeaders();
    return requestRaw<BvReportSendResult>('/send', { method: 'POST', headers, body: form });
  },

  history: (billId: string): Promise<ApiResponse<BvReportSend[]>> =>
    request<BvReportSend[]>(`/history/${billId}`),

  /** The report file Super Admin chose for emails; the PDF if it can't be read. */
  attachmentFormat: async (): Promise<ReportAttachmentFormat> => {
    const res = await sysConfigApiService.get(REPORT_ATTACHMENT_CONFIG);
    return reportAttachmentFormat(res.success ? res.data?.value : null);
  },

  pdfLink: (sendId: string): Promise<ApiResponse<{ url: string }>> =>
    request<{ url: string }>(`/sends/${sendId}/pdf`),

  createBatch: (body: {
    billIds: string[]; recipientsByBill: Record<string, string[]>; templateId: string | null; accountId: string;
  }): Promise<ApiResponse<{ batchId: string }>> =>
    request<{ batchId: string }>('/batches', { method: 'POST', body: JSON.stringify(body) }),

  listBatches: (): Promise<ApiResponse<ReportBatchSummary[]>> => request<ReportBatchSummary[]>('/batches'),

  getBatch: (id: string): Promise<ApiResponse<{ batch: ReportBatchSummary; items: ReportBatchItemView[] }>> =>
    request<{ batch: ReportBatchSummary; items: ReportBatchItemView[] }>(`/batches/${id}`),

  batchAction: (id: string, action: 'pause' | 'resume' | 'cancel'): Promise<ApiResponse<{ ok: true }>> =>
    request<{ ok: true }>(`/batches/${id}/${action}`, { method: 'POST' }),

  /** Queues a finished batch's failed bills (minus any sent since) as a new batch. */
  resendFailed: (id: string): Promise<ApiResponse<{ batchId: string; queued: number; alreadySent: number }>> =>
    request<{ batchId: string; queued: number; alreadySent: number }>(`/batches/${id}/resend-failed`, { method: 'POST' }),
};
